import { Checkbox } from '../../forms/Checkbox';
import { Cell } from 'react-aria-components';
import styles from './CheckboxCell.module.scss';
import { useBetaDataTableContext } from './Context';
import { buildStickyProps, useStickyColumnsContext } from './useStickyColumns';

const defaultContextMenuHeight = 370;
const defaultContextMenuWidth = 250;

type CheckboxCellProps = {
  /** Unique identifier for the row this checkbox belongs to */
  id: string;
  /** Whether to hide the checkbox but maintain cell space */
  hidden?: boolean;
  /** Expected height of the context menu for viewport boundary detection */
  contextMenuHeight?: number;
  /** Expected width of the context menu for viewport boundary detection */
  contextMenuWidth?: number;
};

export const CheckboxCell = ({
  id,
  hidden,
  contextMenuHeight = defaultContextMenuHeight,
  contextMenuWidth = defaultContextMenuWidth,
}: CheckboxCellProps) => {
  const {
    isChecked: _isChecked,
    checkRow,
    uncheckRow,
    setContextMenuCoords,
  } = useBetaDataTableContext();
  const { getStickyInfo, canScrollLeft, canScrollRight } =
    useStickyColumnsContext();

  const isChecked = _isChecked(id);
  const { style: stickyStyle, ...stickyDataAttrs } = buildStickyProps({
    info: getStickyInfo(0),
    canScrollLeft,
    canScrollRight,
  });

  const onChange = () => {
    if (isChecked) {
      uncheckRow(id);
    } else {
      checkRow(id);
    }
  };

  const showContextMenu = (event: React.MouseEvent) => {
    const x = Math.max(
      0,
      event.clientX + contextMenuWidth <= window.innerWidth
        ? event.clientX
        : event.clientX - contextMenuWidth
    );

    const y = Math.max(
      0,
      event.clientY + contextMenuHeight <= window.innerHeight
        ? event.clientY
        : event.clientY - contextMenuHeight
    );

    event.preventDefault();
    setContextMenuCoords([x, y]);
  };

  return (
    <Cell
      className={styles.checkboxCell}
      style={stickyStyle}
      {...stickyDataAttrs}
    >
      {!hidden && (
        <div onContextMenu={showContextMenu}>
          <Checkbox
            className={styles.checkboxCellCheckbox}
            aria-label="Select"
            isSelected={isChecked}
            onChange={onChange}
            value={id}
            slot="selection"
          />
        </div>
      )}
    </Cell>
  );
};
